import { Button, Checkbox, Input } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Row, Seg } from "~/common/account/panels";
import { ApiError, api, type NotificationKind, type NotificationSettings } from "~/core/api";
import { useI18n } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";

const QUERY = [...keys.me, "notification-settings"];

/**
 * The person's own choices, across every account they are in. Every
 * notification reaches the bell; these decide which of them also go into the
 * letter, how often it is sent, and where.
 */
export function NotificationsPanel() {
	const state = useQuery({ queryKey: QUERY, queryFn: api.notificationSettings, ...read });
	return <Async state={state}>{(settings) => <Form settings={settings} />}</Async>;
}

function Form({ settings }: { settings: NotificationSettings }) {
	const { t, locale } = useI18n();
	const queryClient = useQueryClient();
	const failed = (error: unknown) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
	const save = useMutation({
		mutationFn: api.saveNotificationSettings,
		onSuccess: (next) => {
			queryClient.setQueryData(QUERY, next);
			toast.success(t("notifications.saved"));
		},
		onError: failed,
	});
	const mailed = new Set(settings.mailed);
	function toggle(kind: NotificationKind, on: boolean) {
		save.mutate({ mailed: settings.kinds.filter((k) => (k === kind ? on : mailed.has(k))) });
	}

	return (
		<fieldset disabled={save.isPending} className="bare-fieldset">
			{settings.kinds.map((kind) => (
				<Row key={kind} label={t(`notifications.kind.${kind}`)} hint={t(`notifications.kind.${kind}.hint`)}>
					<Checkbox
						id={`notify-${kind}`}
						checked={mailed.has(kind)}
						onCheckedChange={(checked) => toggle(kind, checked === true)}
					/>
					<label htmlFor={`notify-${kind}`}>{t("notifications.email")}</label>
				</Row>
			))}
			<Row label={t("notifications.digest")} hint={t("notifications.digestHint")}>
				<Seg
					label={t("notifications.digest")}
					value={settings.digest}
					onChange={(digest) => save.mutate({ digest })}
					options={[
						{ value: "hourly" as const, label: t("notifications.digest.hourly") },
						{ value: "daily" as const, label: t("notifications.digest.daily") },
					]}
				/>
			</Row>
			<Address settings={settings} onReset={() => save.mutate({ reset_email: true })} />
		</fieldset>
	);
}

/**
 * Another address takes effect only once a code sent to it comes back, so
 * nobody can point Ondit's mail at an inbox they do not read.
 */
function Address({ settings, onReset }: { settings: NotificationSettings; onReset: () => void }) {
	const { t, locale } = useI18n();
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [email, setEmail] = useState("");
	const [sentTo, setSentTo] = useState<string | null>(null);
	const [code, setCode] = useState("");
	const failed = (error: unknown) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
	const send = useMutation({
		mutationFn: () => api.sendNotificationEmailCode(email.trim()),
		onSuccess: () => setSentTo(email.trim()),
		onError: failed,
	});
	const confirm = useMutation({
		mutationFn: () => api.confirmNotificationEmail(code.trim()),
		onSuccess: (next) => {
			queryClient.setQueryData(QUERY, next);
			toast.success(t("notifications.address.confirmed", { email: next.email ?? "" }));
			setEditing(false);
			setSentTo(null);
			setEmail("");
			setCode("");
		},
		onError: failed,
	});

	const current = settings.email ?? t("notifications.address.signIn", { email: settings.sign_in_email });
	const hint = !settings.email && !settings.sign_in_verified ? t("notifications.address.unverified") : current;

	return (
		<>
			<Row label={t("notifications.address")} hint={hint}>
				{editing ? null : settings.email ? (
					<Button variant="outline" onClick={onReset}>
						{t("notifications.address.reset")}
					</Button>
				) : null}
				{editing ? null : (
					<Button variant="outline" onClick={() => setEditing(true)}>
						{t("notifications.address.change")}
					</Button>
				)}
			</Row>
			{editing && !sentTo ? (
				<form
					className="account-row"
					onSubmit={(event) => {
						event.preventDefault();
						send.mutate();
					}}
				>
					<span className="account-row-label">{t("notifications.address.new")}</span>
					<div className="account-row-control">
						<Input
							type="email"
							autoComplete="email"
							required
							value={email}
							aria-label={t("notifications.address.new")}
							onChange={(event) => setEmail(event.target.value)}
						/>
						<Button type="submit" disabled={send.isPending || !email.trim()}>
							{t("notifications.address.send")}
						</Button>
					</div>
				</form>
			) : null}
			{sentTo ? (
				<form
					className="account-row"
					onSubmit={(event) => {
						event.preventDefault();
						confirm.mutate();
					}}
				>
					<div className="account-row-text">
						<span className="account-row-label">{t("notifications.address.code")}</span>
						<span className="hint">{t("notifications.address.sent", { email: sentTo })}</span>
					</div>
					<div className="account-row-control">
						<Input
							inputMode="numeric"
							autoComplete="one-time-code"
							maxLength={6}
							required
							value={code}
							aria-label={t("notifications.address.code")}
							onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
						/>
						<Button type="submit" disabled={confirm.isPending || code.length !== 6}>
							{t("notifications.address.confirm")}
						</Button>
					</div>
				</form>
			) : null}
		</>
	);
}
